import React from "react";
import { Tabs, ITabProps } from "component-library";

import { FormTab, IEditBasicInfoContent } from "./EditBasicInfoContent";

import { BasicInfoForm } from "./BasicInfoForm";
import { AdditionalInfoForm } from "./AdditionalInfoForm";

import {
  IEditBasicInfoSharedState,
  EditBasicInfoSharedStateProvider,
} from "../../../../contexts/EditBasicInfoContext";
import {
  IBasicInfoContract,
  IFacetCategoryContract,
  ILocationContract,
  IGoogleLocationContract,
} from "../../models/contracts";
import {
  FacetsViewModel,
  BasicInfoViewModel,
  IDancingViewModel,
} from "../../models/views";
import { IDancingViewModelKeys } from "../../models/views/DancingViewModel";
import { BasicInfoConverter, FacetConverter } from "../../converters";
import { StepsPagination } from "../../../Common/StepsPagination";

import "./EditBasicInfo.scss";

export interface IEditBasicInfoProps {
  model: IBasicInfoContract;
  facets: IFacetCategoryContract[];
  applyChanges: (model: IBasicInfoContract) => void;
  searchForLocation: (value: string) => Promise<ILocationContract[]>;
  roundLocation: (placeId: string) => Promise<IGoogleLocationContract>;
  content: IEditBasicInfoContent;
  isVisibleSection: (name: string) => boolean;
}

interface IEditBasicInfoState {
  selectedTab: FormTab;
  model: BasicInfoViewModel;
  facets: FacetsViewModel;
  sharedState: IEditBasicInfoSharedState;
}

const TABS_FLOW = [FormTab.basic, FormTab.additional];

export class EditBasicInfo extends React.Component<
  IEditBasicInfoProps,
  IEditBasicInfoState
> {
  constructor(props: IEditBasicInfoProps) {
    super(props);
    const facets = FacetConverter.convertToView(props.facets);
    const model = BasicInfoConverter.convertToView(props.model, facets);

    this.state = {
      facets,
      model,
      selectedTab: FormTab.basic,
      sharedState: {
        isPlayingAgeWarned: false,
        isPregnantDateInvalid: false,
        isPregnant: model.isPregnant,
        isDancerMeasurementsVisible:
          this.checkDancerMeasurementsVisibility(model),
        changeSharedState: this.changeSharedState,
        isVisibleSection: props.isVisibleSection,
      },
    };
  }

  public render() {
    const { content } = this.props;
    const { sharedState } = this.state;

    return (
      <React.Fragment>
        <EditBasicInfoSharedStateProvider value={sharedState}>
          {this.renderTabs(content)}
          {this.renderForm()}
          {this.renderTabSwitchControl(content)}
        </EditBasicInfoSharedStateProvider>
      </React.Fragment>
    );
  }

  public isFormValid = () =>
    !Boolean(this.state.sharedState.isPregnantDateInvalid);

  private getTabs = (content: IEditBasicInfoContent) => {
    const tabs: ITabProps[] = [];

    const { selectedTab } = this.state;

    tabs.push({
      text: content.basicTab,
      click: () => this.selectTab(FormTab.basic),
      isActive: selectedTab === FormTab.basic,
    });

    tabs.push({
      text: content.additionalTab,
      click: () => this.selectTab(FormTab.additional),
      isActive: selectedTab === FormTab.additional,
    });

    return tabs;
  };

  private scrollTo = (
    selector: string,
    setting: ScrollIntoViewOptions = {}
  ) => {
    const element = document.querySelector<HTMLDivElement>(selector);
    element && element.scrollIntoView({ behavior: "smooth", ...setting });
  };

  private renderTabs = (content: IEditBasicInfoContent) => (
    <div className="c-basic-info-form-container__tabs">
      <Tabs tabs={this.getTabs(content)} />
    </div>
  );

  private renderForm = () => {
    const { content, searchForLocation, roundLocation } = this.props;
    const { model, facets } = this.state;

    let result: JSX.Element | null = null;

    switch (this.state.selectedTab) {
      case FormTab.basic:
        result = (
          <BasicInfoForm
            searchForLocation={searchForLocation}
            roundLocation={roundLocation}
            content={content.basicInfoForm}
            applyChanges={this.applyChanges}
            model={model}
            facets={facets}
          />
        );
        break;
      case FormTab.additional:
        result = (
          <AdditionalInfoForm
            content={content.additionalInfoForm}
            applyChanges={this.applyChanges}
            model={model}
            facets={facets}
          />
        );
        break;
    }

    return result;
  };

  private renderTabSwitchControl = (content: IEditBasicInfoContent) => {
    const { selectedTab } = this.state;

    const tabIndex = TABS_FLOW.findIndex((tab) => tab === selectedTab);

    const isLastPage = tabIndex >= TABS_FLOW.length - 1;
    const isFirstPage = tabIndex === 0;

    return (
      <StepsPagination
        moveToPrev={this.moveToPreviousStep}
        moveToNext={this.moveToNextStep}
        previousButtonText={content.switchToPreviousTabText}
        nextButtonText={content.switchToNextTabText}
        isPrevStepAvailable={!isFirstPage}
        isNextStepAvailable={!isLastPage}
      />
    );
  };

  private moveToNextStep = () => {
    const { selectedTab } = this.state;
    const nextTabIndex = TABS_FLOW.findIndex((tab) => tab === selectedTab) + 1;

    if (nextTabIndex < TABS_FLOW.length) {
      this.scrollTo(".c-tabs__container", { block: "end" });
      this.setState({ selectedTab: TABS_FLOW[nextTabIndex] });
    }
  };

  private moveToPreviousStep = () => {
    const { selectedTab } = this.state;
    const prevTabIndex = TABS_FLOW.findIndex((tab) => tab === selectedTab) - 1;

    if (prevTabIndex >= 0) {
      this.scrollTo(".c-tabs__container", { block: "end" });
      this.setState({ selectedTab: TABS_FLOW[prevTabIndex] });
    }
  };

  private selectTab = (selectedTab: FormTab) => this.setState({ selectedTab });

  private applyChanges = (model: BasicInfoViewModel) => {
    const result = BasicInfoConverter.convertToBusiness(model);
    this.props.applyChanges(result);
  };

  private changeSharedState = (
    sharedFormState: IEditBasicInfoSharedState,
    callback?: () => void
  ) =>
    this.setState(
      (state) => ({
        sharedState: { ...state.sharedState, ...sharedFormState },
      }),
      callback
    );

  private checkDancerMeasurementsVisibility = (
    model: IDancingViewModel
  ): boolean => {
    const isMeasurementsInModel = IDancingViewModelKeys.some((key) =>
      Object.keys(model).some((modelKey) => modelKey === key)
    );

    return isMeasurementsInModel;
  };

  static getDerivedStateFromProps(
    nextProps: IEditBasicInfoProps,
    prevState: IEditBasicInfoState
  ) {
    const facets = FacetConverter.convertToView(nextProps.facets);
    const model = BasicInfoConverter.convertToView(nextProps.model, facets);

    return {
      ...prevState,
      facets,
      model,
    };
  }
}

export default EditBasicInfo;
